/*!
 * Collection redesign — Beerbani
 * Vanilla JS, no dependencies, loaded with `defer`.
 * - Filters/sort update the grid through Shopify's Section Rendering API (no full page reload)
 * - Discount filter + Discount sort run in the browser from each card's data-discount
 * - Infinite scroll, image slider dots/arrows, wishlist (this device), AJAX add to cart + stepper
 */
(() => {
  'use strict';

  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SHOP_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const THEME_CART_SECTIONS = ['cart-icon-bubble']; // swapped after add-to-cart when the theme has them

  const store = {
    get(key, fallback) {
      try {
        const value = window.localStorage.getItem(key);
        return value === null ? fallback : JSON.parse(value);
      } catch (e) {
        return fallback;
      }
    },
    set(key, value) {
      try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage blocked */ }
    }
  };

  const debounce = (fn, ms) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), ms);
    };
  };

  function moneyFormatter(currency) {
    let fmt = null;
    try {
      fmt = new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency', currency: currency || 'INR', maximumFractionDigits: 0
      });
    } catch (e) {
      fmt = null;
    }
    return (cents) => (fmt ? fmt.format(cents / 100) : String(Math.round(cents / 100)));
  }

  function initCollection(root) {
    if (!root || root.dataset.crReady === 'true') return;
    root.dataset.crReady = 'true';
    root.classList.add('is-js');

    const sectionId = root.dataset.sectionId;
    const perPage = parseInt(root.dataset.perPage, 10) || 24;
    const defaultSort = root.dataset.defaultSort || '';
    const money = moneyFormatter(root.dataset.currency);
    const q = (sel, el = root) => el.querySelector(sel);
    const qa = (sel, el = root) => Array.from(el.querySelectorAll(sel));
    const form = q('[data-cr-form]');
    const groups = q('[data-cr-groups]');
    const toastEl = q('[data-cr-toast]');

    const state = {
      sort: defaultSort,
      discount: 0,
      nextUrl: '',
      loadingMore: false,
      token: 0,
      ctrl: { page: null, more: null }
    };

    function readUrlState() {
      const p = new URLSearchParams(window.location.search);
      state.sort = p.get('cr_sort') === 'discount' ? 'discount' : (p.get('sort_by') || defaultSort);
      state.discount = parseInt(p.get('cr_discount'), 10) || 0;
    }

    /* ---------------- URLs & fetching ---------------- */

    function finishUrl(path, params) {
      ['page', 'section_id', 'cr_sort', 'cr_discount', 'sort_by'].forEach((k) => params.delete(k));
      if (state.sort === 'discount') params.set('cr_sort', 'discount');
      else if (state.sort && state.sort !== defaultSort) params.set('sort_by', state.sort);
      if (state.discount) params.set('cr_discount', String(state.discount));
      const qs = params.toString();
      return path + (qs ? `?${qs}` : '');
    }

    function buildUrl() {
      const params = new URLSearchParams();
      if (form) {
        new FormData(form).forEach((value, key) => {
          if (String(value).trim() !== '') params.append(key, value);
        });
      }
      return finishUrl(window.location.pathname, params);
    }

    function urlFromHref(href) {
      const u = new URL(href, window.location.origin);
      return finishUrl(u.pathname, u.searchParams);
    }

    async function fetchSection(url, kind) {
      if (state.ctrl[kind]) state.ctrl[kind].abort();
      const ctrl = new AbortController();
      state.ctrl[kind] = ctrl;
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', sectionId);
      const res = await fetch(u.toString(), { signal: ctrl.signal, credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      return new DOMParser().parseFromString(html, 'text/html');
    }

    async function render(url, push = true) {
      const token = ++state.token;
      if (state.ctrl.more) state.ctrl.more.abort();
      state.loadingMore = false;
      root.classList.add('is-loading');
      try {
        const doc = await fetchSection(url, 'page');
        if (token !== state.token) return;
        swapGroups(doc);
        const fresh = doc.querySelector('[data-cr-results]');
        const current = q('[data-cr-results]');
        if (fresh && current) current.replaceWith(document.adoptNode(fresh));
        if (push) window.history.pushState({ cr: true }, '', url);
        else window.history.replaceState({ cr: true }, '', url);
        afterResults();
      } catch (err) {
        if (err.name !== 'AbortError' && token === state.token) window.location.assign(url);
      } finally {
        if (token === state.token) root.classList.remove('is-loading');
      }
    }

    function afterResults() {
      const more = q('[data-cr-more]');
      state.nextUrl = more ? (more.dataset.nextUrl || '') : '';
      const items = qa('[data-cr-grid] > li');
      items.forEach((li, i) => { li.dataset.i = String(i); });
      hydrate(items);
      syncDiscountUI();
      applyClient();
      observeMore();
      updateFilterCount();
      if (state.sort === 'discount') loadAll();
      else fillVisible();
    }

    /* ---------------- Client-side discount filter / sort ---------------- */

    const countVisible = () => qa('[data-cr-grid] > li:not([hidden])').length;

    function applyClient() {
      const grid = q('[data-cr-grid]');
      if (!grid) return 0;
      const items = Array.from(grid.children);
      let visible = 0;
      items.forEach((li) => {
        const show = (parseInt(li.dataset.discount, 10) || 0) >= state.discount;
        li.hidden = !show;
        if (show) visible += 1;
      });
      if (state.sort === 'discount') {
        items.sort((a, b) => (b.dataset.discount - a.dataset.discount) || (a.dataset.i - b.dataset.i));
        grid.append(...items);
      }
      const empty = q('[data-cr-empty]');
      if (empty) empty.hidden = !(visible === 0 && !state.nextUrl && !state.loadingMore);
      return visible;
    }

    function syncDiscountUI() {
      qa('[data-cr-discount]').forEach((input) => { input.checked = Number(input.value) === state.discount; });
      const badge = q('[data-cr-discount-badge]');
      if (badge) badge.hidden = !state.discount;
      const chip = q('[data-cr-chip-discount]');
      if (chip) {
        chip.hidden = !state.discount;
        const text = q('[data-cr-chip-text]', chip);
        if (text) text.textContent = `${state.discount}% or more off`;
      }
    }

    function setDiscount(value) {
      state.discount = value;
      syncDiscountUI();
      applyClient();
      window.history.replaceState({ cr: true }, '', buildUrl());
      updateFilterCount();
      fillVisible();
    }

    /* ---------------- Infinite scroll ---------------- */

    async function loadMore() {
      if (state.loadingMore || !state.nextUrl) return false;
      state.loadingMore = true;
      const token = state.token;
      const more = q('[data-cr-more]');
      if (more) more.classList.add('is-loading');
      let ok = false;
      try {
        const doc = await fetchSection(state.nextUrl, 'more');
        if (token === state.token) {
          const grid = q('[data-cr-grid]');
          const start = grid ? grid.children.length : 0;
          const items = Array.from(doc.querySelectorAll('[data-cr-grid] > li')).map((li) => document.adoptNode(li));
          items.forEach((li, i) => { li.dataset.i = String(start + i); });
          if (grid) grid.append(...items);
          hydrate(items);
          const nextMore = doc.querySelector('[data-cr-more]');
          state.nextUrl = nextMore ? (nextMore.dataset.nextUrl || '') : '';
          if (more) more.dataset.nextUrl = state.nextUrl;
          ok = true;
        }
      } catch (err) {
        ok = false;
      } finally {
        state.loadingMore = false;
        if (more) more.classList.remove('is-loading');
      }
      if (ok) applyClient();
      return ok;
    }

    let observer = null;
    function observeMore() {
      if (observer) observer.disconnect();
      const more = q('[data-cr-more]');
      if (!more || !state.nextUrl || !('IntersectionObserver' in window)) return;
      observer = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        loadMore().then((ok) => {
          if (!state.nextUrl) { observer.disconnect(); return; }
          if (ok) { observer.unobserve(more); observer.observe(more); } // fires again if still in view
        });
      }, { rootMargin: '0px 0px 900px 0px' });
      observer.observe(more);
    }

    const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

    // Loads pages until `needMore()` is false. If infinite scroll is already loading a page, waits for it.
    async function loadWhile(needMore, maxPages) {
      const token = state.token;
      let guard = 0;
      while (token === state.token && state.nextUrl && needMore() && guard < maxPages) {
        guard += 1;
        if (await loadMore()) continue;
        if (!state.loadingMore) break;
        await wait(150);
      }
    }

    const fillVisible = () => loadWhile(() => state.discount > 0 && countVisible() < perPage, 40);
    const loadAll = () => loadWhile(() => true, 80);

    /* ---------------- Sort ---------------- */

    function syncSortUI() {
      let label = '';
      qa('[data-cr-sort]').forEach((btn) => {
        const on = btn.dataset.crSort === state.sort;
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
        if (on && !label) label = btn.textContent.trim();
      });
      if (label) qa('[data-cr-sort-current]').forEach((el) => { el.textContent = label; });
    }

    function setSort(value) {
      if (value === state.sort) return;
      state.sort = value;
      syncSortUI();
      render(buildUrl());
    }

    /* ---------------- Filter groups ---------------- */

    function syncGroupAria() {
      const desktop = DESKTOP.matches;
      qa('[data-cr-group]').forEach((g) => {
        const btn = q('[data-cr-group-toggle]', g);
        if (!btn) return;
        const on = desktop ? g.classList.contains('is-open') : g.classList.contains('is-active');
        btn.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
    }

    function toggleGroup(btn) {
      const group = btn.closest('[data-cr-group]');
      if (!group) return;
      if (DESKTOP.matches) group.classList.toggle('is-open');
      else qa('[data-cr-group]').forEach((g) => g.classList.toggle('is-active', g === group));
      syncGroupAria();
    }

    function swapGroups(doc) {
      const fresh = doc.querySelector('[data-cr-groups]');
      if (!fresh || !groups) return;
      const open = new Set(qa('[data-cr-group].is-open').map((g) => g.dataset.crGroup));
      const activeEl = q('[data-cr-group].is-active');
      const active = activeEl ? activeEl.dataset.crGroup : null;
      const focused = groups.contains(document.activeElement) ? document.activeElement : null;
      const focusKey = focused && focused.name
        ? { name: focused.name, value: focused.value, type: focused.type }
        : null;
      let rangeKey = null;
      if (focused && focused.hasAttribute('data-cr-range-min')) rangeKey = '[data-cr-range-min]';
      if (focused && focused.hasAttribute('data-cr-range-max')) rangeKey = '[data-cr-range-max]';

      groups.innerHTML = fresh.innerHTML;
      paintAllRanges();
      if (rangeKey) {
        const el = groups.querySelector(rangeKey);
        if (el) el.focus({ preventScroll: true });
      }

      qa('[data-cr-group]').forEach((g) => {
        g.classList.toggle('is-open', open.has(g.dataset.crGroup));
        if (active) g.classList.toggle('is-active', g.dataset.crGroup === active);
      });
      syncGroupAria();
      syncDiscountUI();

      if (focusKey && window.CSS && CSS.escape) {
        const sel = focusKey.type === 'checkbox'
          ? `[name="${CSS.escape(focusKey.name)}"][value="${CSS.escape(focusKey.value)}"]`
          : `[name="${CSS.escape(focusKey.name)}"]`;
        const el = groups.querySelector(sel);
        if (el) {
          if (focusKey.type === 'number') el.value = focusKey.value; // keep what the shopper is typing
          el.focus({ preventScroll: true });
        }
      }
    }

    /* ---------------- Price slider ---------------- */

    function paintRange(wrap) {
      const lo = q('[data-cr-range-min]', wrap);
      const hi = q('[data-cr-range-max]', wrap);
      if (!lo || !hi) return;
      const max = Number(lo.max) || 1;
      const a = Math.min(Number(lo.value), Number(hi.value));
      const b = Math.max(Number(lo.value), Number(hi.value));
      wrap.style.setProperty('--lo', `${(a / max) * 100}%`);
      wrap.style.setProperty('--hi', `${(b / max) * 100}%`);
      // keep the handle you can still drag on top when both meet at an end
      lo.style.zIndex = a >= max - Number(lo.step || 1) ? '3' : '2';
      hi.style.zIndex = '2';
    }

    function rangeToBoxes(input) {
      const wrap = input.closest('[data-cr-range]');
      const group = input.closest('[data-cr-group]');
      if (!wrap || !group) return;
      const lo = q('[data-cr-range-min]', wrap);
      const hi = q('[data-cr-range-max]', wrap);
      const step = Number(lo.step) || 1;
      const max = Number(lo.max);
      if (input === lo && Number(lo.value) > Number(hi.value) - step) lo.value = String(Math.max(0, Number(hi.value) - step));
      if (input === hi && Number(hi.value) < Number(lo.value) + step) hi.value = String(Math.min(max, Number(lo.value) + step));
      const minBox = q('[data-cr-price="min"]', group);
      const maxBox = q('[data-cr-price="max"]', group);
      if (minBox) minBox.value = Number(lo.value) > 0 ? lo.value : '';
      if (maxBox) maxBox.value = Number(hi.value) < max ? hi.value : '';
      paintRange(wrap);
    }

    function boxesToRange(box) {
      const group = box.closest('[data-cr-group]');
      const wrap = group && q('[data-cr-range]', group);
      if (!wrap) return;
      const lo = q('[data-cr-range-min]', wrap);
      const hi = q('[data-cr-range-max]', wrap);
      const minBox = q('[data-cr-price="min"]', group);
      const maxBox = q('[data-cr-price="max"]', group);
      lo.value = minBox && minBox.value !== '' ? minBox.value : '0';
      hi.value = maxBox && maxBox.value !== '' ? maxBox.value : lo.max;
      paintRange(wrap);
    }

    const paintAllRanges = () => qa('[data-cr-range]').forEach(paintRange);

    function updateFilterCount() {
      if (!form) return;
      let n = qa('input[type="checkbox"]:checked', form).length;
      if (qa('[data-cr-price]', form).some((i) => i.value !== '')) n += 1;
      qa('[data-cr-filter-count]').forEach((el) => {
        el.textContent = String(n);
        el.hidden = n === 0;
      });
      qa('[data-cr-filter-summary]').forEach((el) => {
        el.textContent = n ? `${n} ${n === 1 ? 'filter' : 'filters'} applied` : 'No filters applied';
      });
    }

    /* ---------------- Mobile panels ---------------- */

    let lastFocus = null;
    const lockScroll = (on) => document.documentElement.classList.toggle('cr-lock', on);

    function openFilters() {
      lastFocus = document.activeElement;
      root.classList.add('is-filters-open');
      lockScroll(true);
      const first = q('.cr-filters [data-cr-group-toggle]');
      if (first) first.focus({ preventScroll: true });
    }
    function closeFilters() {
      if (!root.classList.contains('is-filters-open')) return;
      root.classList.remove('is-filters-open');
      lockScroll(false);
      if (lastFocus) lastFocus.focus({ preventScroll: true });
    }
    function openSort() {
      const sheet = q('[data-cr-sort-sheet]');
      if (!sheet) return;
      lastFocus = document.activeElement;
      sheet.hidden = false;
      lockScroll(true);
      const current = q('[data-cr-sort-sheet] [aria-checked="true"]') || q('[data-cr-sort-sheet] [data-cr-sort]');
      if (current) current.focus({ preventScroll: true });
    }
    function closeSort() {
      const sheet = q('[data-cr-sort-sheet]');
      if (!sheet || sheet.hidden) return;
      sheet.hidden = true;
      lockScroll(false);
      if (lastFocus) lastFocus.focus({ preventScroll: true });
    }

    function setView(value) {
      const v = value === '1' ? '1' : '2';
      root.dataset.view = v;
      store.set('cr-view', v);
      qa('[data-cr-view]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.crView === v ? 'true' : 'false'));
    }

    /* ---------------- Cards: wishlist & slider ---------------- */

    function setWish(btn, on) {
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Save to wishlist');
    }
    function hydrate(items) {
      watchSlides(items);
      const wished = new Set(store.get('cr-wishlist', []));
      items.forEach((li) => {
        const btn = li.querySelector('[data-cr-wish]');
        if (btn) setWish(btn, wished.has(btn.dataset.crWish));
      });
    }
    function toggleWish(btn) {
      const list = store.get('cr-wishlist', []);
      const handle = btn.dataset.crWish;
      const i = list.indexOf(handle);
      if (i === -1) list.push(handle);
      else list.splice(i, 1);
      store.set('cr-wishlist', list);
      setWish(btn, i === -1);
    }

    const slideIndex = (track) => Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
    function goTo(track, index) {
      const n = track.children.length;
      if (!n) return;
      const i = ((index % n) + n) % n;
      track.scrollTo({ left: i * track.clientWidth, behavior: REDUCED.matches ? 'auto' : 'smooth' });
    }
    // Load the 2nd/3rd slider images just before a card scrolls into view, so swiping never shows a blank.
    // (Lazy images inside horizontal scrollers otherwise wait until they are swiped into view.)
    const slideObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
          slideObserver.unobserve(entry.target);
        });
      }, { rootMargin: '300px 0px' })
      : null;
    function watchSlides(items) {
      items.forEach((li) => {
        const track = li.querySelector('[data-cr-slides]');
        if (!track || track.children.length < 2) return;
        if (slideObserver) slideObserver.observe(track);
        else track.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      });
    }

    // Category circles: arrows on desktop when there are more than fit in view
    const catsTrack = q('[data-cr-cats-track]');
    const catsPrev = q('[data-cr-cats-prev]');
    const catsNext = q('[data-cr-cats-next]');
    function updateCatsArrows() {
      if (!catsTrack || !catsPrev || !catsNext) return;
      const max = catsTrack.scrollWidth - catsTrack.clientWidth;
      catsPrev.hidden = catsTrack.scrollLeft <= 2;
      catsNext.hidden = catsTrack.scrollLeft >= max - 2;
    }
    function scrollCats(dir) {
      if (!catsTrack) return;
      catsTrack.scrollBy({ left: dir * catsTrack.clientWidth * 0.85, behavior: REDUCED.matches ? 'auto' : 'smooth' });
    }

    function onSlideScroll(e) {
      const track = e.target;
      if (track === catsTrack) {
        if (!catsTrack.crRaf) catsTrack.crRaf = requestAnimationFrame(() => { catsTrack.crRaf = 0; updateCatsArrows(); });
        return;
      }
      if (!(track instanceof Element) || !track.matches('[data-cr-slides]') || track.crRaf) return;
      track.crRaf = requestAnimationFrame(() => {
        track.crRaf = 0;
        const i = slideIndex(track);
        qa('[data-cr-dot]', track.parentElement).forEach((dot, k) => {
          dot.setAttribute('aria-current', k === i ? 'true' : 'false');
        });
      });
    }

    /* ---------------- Cart ---------------- */

    const themeSections = () => THEME_CART_SECTIONS.filter((id) => document.getElementById(id));

    function swapThemeSections(sections) {
      if (!sections) return;
      Object.keys(sections).forEach((id) => {
        const target = document.getElementById(id);
        if (!target || !sections[id]) return;
        const doc = new DOMParser().parseFromString(sections[id], 'text/html');
        const inner = doc.querySelector('.shopify-section');
        target.innerHTML = inner ? inner.innerHTML : sections[id];
      });
    }

    function updateCartUI(cart) {
      qa('[data-cr-cart-count]').forEach((el) => { el.textContent = String(cart.item_count); });
      qa('[data-cr-cart-total]').forEach((el) => { el.textContent = money(cart.total_price); });
      const bar = q('[data-cr-cartbar]');
      if (bar) bar.hidden = cart.item_count === 0;
      root.classList.toggle('has-cart', cart.item_count > 0);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }

    async function getCart() {
      const res = await fetch(`${SHOP_ROOT}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      return res.json();
    }

    function setQty(formEl, qty) {
      const el = q('[data-cr-qty]', formEl);
      if (el) el.textContent = String(qty);
      formEl.classList.toggle('is-in-cart', qty > 0);
    }

    async function addToCart(formEl) {
      const btn = q('.cr-atc', formEl);
      if (btn) btn.classList.add('is-busy');
      try {
        const body = new FormData(formEl);
        const ids = themeSections();
        if (ids.length) {
          body.append('sections', ids.join(','));
          body.append('sections_url', window.location.pathname);
        }
        const res = await fetch(`${SHOP_ROOT}cart/add.js`, {
          method: 'POST',
          body,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
        setQty(formEl, data.quantity || 1);
        swapThemeSections(data.sections);
        updateCartUI(await getCart());
        toast(root.dataset.addedText || 'Added to cart', true);
      } catch (err) {
        toast(err.message || 'Could not add to cart', false);
      } finally {
        if (btn) btn.classList.remove('is-busy');
      }
    }

    async function changeQty(formEl, qty) {
      const id = Number(q('input[name="id"]', formEl).value);
      try {
        const payload = { id, quantity: qty };
        const ids = themeSections();
        if (ids.length) {
          payload.sections = ids;
          payload.sections_url = window.location.pathname;
        }
        const res = await fetch(`${SHOP_ROOT}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'same-origin'
        });
        const cart = await res.json();
        if (!res.ok) throw new Error(cart.description || cart.message || 'Could not update cart');
        const line = (cart.items || []).find((it) => it.variant_id === id);
        setQty(formEl, line ? line.quantity : 0);
        swapThemeSections(cart.sections);
        updateCartUI(cart);
      } catch (err) {
        toast(err.message || 'Could not update cart', false);
        try {
          const cart = await getCart();
          const line = cart.items.find((it) => it.variant_id === id);
          setQty(formEl, line ? line.quantity : 0);
          updateCartUI(cart);
        } catch (e) { /* offline */ }
      }
    }

    const stepTimers = new WeakMap();
    function step(btn) {
      const formEl = btn.closest('[data-cr-atc]');
      if (!formEl) return;
      const current = parseInt(q('[data-cr-qty]', formEl).textContent, 10) || 0;
      const next = Math.max(0, current + Number(btn.dataset.crStep));
      setQty(formEl, next); // instant feedback; request is debounced for rapid taps
      clearTimeout(stepTimers.get(formEl));
      stepTimers.set(formEl, setTimeout(() => changeQty(formEl, next), 350));
    }

    let toastTimer = 0;
    let toastHideTimer = 0;
    function toast(message, ok) {
      if (!toastEl) return;
      q('[data-cr-toast-text]', toastEl).textContent = message;
      toastEl.classList.toggle('is-error', !ok);
      clearTimeout(toastTimer);
      clearTimeout(toastHideTimer);
      toastEl.hidden = false;
      requestAnimationFrame(() => toastEl.classList.add('is-visible'));
      toastTimer = setTimeout(() => {
        toastEl.classList.remove('is-visible');
        toastHideTimer = setTimeout(() => { toastEl.hidden = true; }, 250);
      }, 2600);
    }

    /* ---------------- Events ---------------- */

    root.addEventListener('click', (e) => {
      const t = e.target.closest(
        '[data-cr-sort],[data-cr-group-toggle],[data-cr-open-filters],[data-cr-close-filters],' +
        '[data-cr-open-sort],[data-cr-close-sort],[data-cr-view],[data-cr-chip],[data-cr-chip-discount],' +
        '[data-cr-clear],[data-cr-prev],[data-cr-next],[data-cr-dot],[data-cr-wish],[data-cr-step],' +
        '[data-cr-cats-prev],[data-cr-cats-next]'
      );
      if (!t || !root.contains(t)) return;

      if (t.hasAttribute('data-cr-cats-prev')) { scrollCats(-1); return; }
      if (t.hasAttribute('data-cr-cats-next')) { scrollCats(1); return; }

      if (t.hasAttribute('data-cr-sort')) { closeSort(); setSort(t.dataset.crSort); return; }
      if (t.hasAttribute('data-cr-group-toggle')) { toggleGroup(t); return; }
      if (t.hasAttribute('data-cr-open-filters')) { openFilters(); return; }
      if (t.hasAttribute('data-cr-close-filters')) { closeFilters(); return; }
      if (t.hasAttribute('data-cr-open-sort')) { openSort(); return; }
      if (t.hasAttribute('data-cr-close-sort')) { closeSort(); return; }
      if (t.hasAttribute('data-cr-view')) { setView(t.dataset.crView); return; }
      if (t.hasAttribute('data-cr-chip-discount')) { setDiscount(0); return; }
      if (t.hasAttribute('data-cr-chip')) { e.preventDefault(); render(urlFromHref(t.href)); return; }
      if (t.hasAttribute('data-cr-clear')) {
        e.preventDefault();
        state.discount = 0;
        render(urlFromHref(t.href));
        return;
      }
      if (t.hasAttribute('data-cr-prev') || t.hasAttribute('data-cr-next')) {
        const track = q('[data-cr-slides]', t.closest('.cr-media'));
        if (track) goTo(track, slideIndex(track) + (t.hasAttribute('data-cr-next') ? 1 : -1));
        return;
      }
      if (t.hasAttribute('data-cr-dot')) {
        const track = q('[data-cr-slides]', t.closest('.cr-media'));
        if (track) goTo(track, Number(t.dataset.crDot));
        return;
      }
      if (t.hasAttribute('data-cr-wish')) { toggleWish(t); return; }
      if (t.hasAttribute('data-cr-step')) step(t);
    });

    root.addEventListener('submit', (e) => {
      const atc = e.target.closest('[data-cr-atc]');
      if (atc) { e.preventDefault(); addToCart(atc); return; }
      if (e.target === form) { e.preventDefault(); render(buildUrl()); }
    });

    if (form) {
      form.addEventListener('change', (e) => {
        const el = e.target;
        if (el.matches('[data-cr-discount]')) {
          setDiscount(el.checked ? Number(el.value) : 0);
          return;
        }
        if (el.matches('[data-cr-price]')) return; // handled by the debounced input listener
        if (el.matches('[data-cr-range-min], [data-cr-range-max]')) {
          // slider released (or moved with the keyboard): apply now
          rangeToBoxes(el);
          updateFilterCount();
          render(buildUrl());
          return;
        }
        updateFilterCount();
        render(buildUrl());
      });
      const onPrice = debounce(() => { updateFilterCount(); render(buildUrl()); }, 600);
      form.addEventListener('input', (e) => {
        const el = e.target;
        if (el.matches('[data-cr-range-min], [data-cr-range-max]')) { rangeToBoxes(el); return; } // live while dragging
        if (el.matches('[data-cr-price]')) { boxesToRange(el); onPrice(); }
      });
    }

    root.addEventListener('scroll', onSlideScroll, { capture: true, passive: true });
    root.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { closeSort(); closeFilters(); }
    });

    const onBreakpoint = () => {
      if (DESKTOP.matches) { closeFilters(); closeSort(); }
      syncGroupAria();
      updateCatsArrows();
    };
    window.addEventListener('resize', debounce(updateCatsArrows, 150), { passive: true });
    if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', onBreakpoint);
    else if (DESKTOP.addListener) DESKTOP.addListener(onBreakpoint);

    window.addEventListener('popstate', () => {
      readUrlState();
      syncSortUI();
      render(window.location.pathname + window.location.search, false);
    });

    /* ---------------- Init ---------------- */
    readUrlState();
    setView(store.get('cr-view', '2'));
    syncSortUI();
    syncGroupAria();
    updateCatsArrows();
    paintAllRanges();
    afterResults();
  }

  const boot = () => document.querySelectorAll('[data-cr-root]').forEach(initCollection);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: re-initialise when the section is re-rendered
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-cr-root]');
    if (root) initCollection(root);
  });
})();
